import { apiClient } from './api'
import {
  PaymentPayoutBatch,
  CreatePayoutBatchData,
  UpdatePayoutBatchData,
  PayoutLineItem,
  CommissionRecord,
  MerchantSettlement,
  CreateSettlementData,
  UpdateSettlementData,
  SettlementItem,
  PayoutStats,
} from '@/types'

export interface PayoutBatchListResponse {
  success: boolean
  message: string
  data: {
    results: PaymentPayoutBatch[]
    count: number
    next: string | null
    previous: string | null
  }
}

export interface PayoutBatchResponse {
  success: boolean
  message: string
  data: PaymentPayoutBatch
}

export interface PayoutLineItemListResponse {
  success: boolean
  message: string
  data: PayoutLineItem[]
}

export interface CommissionRecordListResponse {
  success: boolean
  message: string
  data: CommissionRecord[]
}

export interface SettlementListResponse {
  success: boolean
  message: string
  data: {
    results: MerchantSettlement[]
    count: number
    next: string | null
    previous: string | null
  }
}

export interface SettlementResponse {
  success: boolean
  message: string
  data: MerchantSettlement
}

export interface PayoutStatsResponse {
  success: boolean
  message: string
  data: PayoutStats
}

export interface SettlementItemListResponse {
  success: boolean
  message: string
  data: SettlementItem[]
}

export interface SettlementItemResponse {
  success: boolean
  message: string
  data: SettlementItem
}

export interface MerchantListResponse {
  success: boolean
  message: string
  data: {
    results: Array<{
      id: number
      company_name: string
      name: string
      email: string
      status: string
      currency: {
        code: string
        name: string
      }
    }>
    count: number
    next: string | null
    previous: string | null
  }
}

export const payoutService = {
  // Payout Batches
  async getPayoutBatches(params?: {
    page?: number
    page_size?: number
    status?: string
    currency?: string
    merchant?: number
  }): Promise<PayoutBatchListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.currency) queryParams.append('currency', params.currency)
    if (params?.merchant) queryParams.append('merchant', params.merchant.toString())
    
    const queryString = queryParams.toString()
    const url = queryString ? `/payout-batches/?${queryString}` : '/payout-batches/'
    
    const response = await apiClient.get(url) as any
    return { success: true, message: 'Payout batches retrieved successfully', data: response }
  },

  async getPayoutBatch(id: number): Promise<PayoutBatchResponse> {
    const response = await apiClient.get(`/payout-batches/${id}/`) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch retrieved successfully', data: response }
  },

  async createPayoutBatch(data: CreatePayoutBatchData): Promise<PayoutBatchResponse> {
    const response = await apiClient.post('/payout-batches/', data) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch created successfully', data: response }
  },

  async updatePayoutBatch(id: number, data: UpdatePayoutBatchData): Promise<PayoutBatchResponse> {
    const response = await apiClient.patch(`/payout-batches/${id}/`, data) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch updated successfully', data: response }
  },

  async calculatePayoutBatchTotals(id: number): Promise<PayoutBatchResponse> {
    const response = await apiClient.post(`/payout-batches/${id}/calculate_totals/`) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch totals calculated successfully', data: response }
  },

  async buildPayoutBatchLineItems(id: number): Promise<PayoutBatchResponse> {
    const response = await apiClient.post(`/payout-batches/${id}/build_line_items/`) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch line items built successfully', data: response }
  },

  async approvePayoutBatch(id: number): Promise<PayoutBatchResponse> {
    const response = await apiClient.post(`/payout-batches/${id}/approve/`) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch approved successfully', data: response }
  },

  async markPayoutBatchPaid(id: number): Promise<PayoutBatchResponse> {
    const response = await apiClient.post(`/payout-batches/${id}/mark_paid/`) as PaymentPayoutBatch
    return { success: true, message: 'Payout batch marked as paid successfully', data: response }
  },

  async getPayoutBatchLineItems(id: number): Promise<PayoutLineItemListResponse> {
    const response = await apiClient.get(`/payout-batches/${id}/line_items/`) as PayoutLineItem[]
    return { success: true, message: 'Payout batch line items retrieved successfully', data: response }
  },

  async getPayoutStats(): Promise<PayoutStatsResponse> {
    const response = await apiClient.get('/payout-batches/stats/') as PayoutStats
    return { success: true, message: 'Payout statistics retrieved successfully', data: response }
  },

  // Settlements
  async getSettlements(params?: {
    page?: number
    page_size?: number
    status?: string
    method?: string
    merchant?: number
  }): Promise<SettlementListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.method) queryParams.append('method', params.method)
    if (params?.merchant) queryParams.append('merchant', params.merchant.toString())
    
    const queryString = queryParams.toString()
    const url = queryString ? `/settlements/?${queryString}` : '/settlements/'
    
    const response = await apiClient.get(url) as any
    return { success: true, message: 'Settlements retrieved successfully', data: response }
  },

  async getSettlement(id: number): Promise<SettlementResponse> {
    const response = await apiClient.get(`/settlements/${id}/`) as MerchantSettlement
    return { success: true, message: 'Settlement retrieved successfully', data: response }
  },

  async createSettlement(data: CreateSettlementData): Promise<SettlementResponse> {
    try {
      const response = await apiClient.post('/settlements/', data) as MerchantSettlement
      return { success: true, message: 'Settlement created successfully', data: response }
    } catch (error: any) {
      console.error('Error creating settlement:', error)
      throw error // Re-throw to let the component handle it
    }
  },

  async updateSettlement(id: number, data: UpdateSettlementData): Promise<SettlementResponse> {
    try {
      const response = await apiClient.patch(`/settlements/${id}/`, data) as MerchantSettlement
      return { success: true, message: 'Settlement updated successfully', data: response }
    } catch (error: any) {
      console.error('Error updating settlement:', error)
      throw error // Re-throw to let the component handle it
    }
  },

  async markSettlementCompleted(id: number): Promise<SettlementResponse> {
    const response = await apiClient.post(`/settlements/${id}/mark_completed/`) as MerchantSettlement
    return { success: true, message: 'Settlement marked as completed successfully', data: response }
  },

  async markSettlementFailed(id: number): Promise<SettlementResponse> {
    const response = await apiClient.post(`/settlements/${id}/mark_failed/`) as MerchantSettlement
    return { success: true, message: 'Settlement marked as failed successfully', data: response }
  },

  // Commission Records
  async getCommissionRecords(params?: {
    page?: number
    page_size?: number
    merchant?: number
    currency?: string
  }): Promise<CommissionRecordListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.merchant) queryParams.append('merchant', params.merchant.toString())
    if (params?.currency) queryParams.append('currency', params.currency)
    
    const queryString = queryParams.toString()
    const url = queryString ? `/commission-records/?${queryString}` : '/commission-records/'
    
    const response = await apiClient.get(url) as CommissionRecord[]
    return { success: true, message: 'Commission records retrieved successfully', data: response }
  },

  async getCommissionRecordsByMerchant(merchantId: number): Promise<CommissionRecordListResponse> {
    const response = await apiClient.get(`/commission-records/by_merchant/?merchant_id=${merchantId}`) as CommissionRecord[]
    return { success: true, message: 'Commission records by merchant retrieved successfully', data: response }
  },

  async getCommissionRecordsBySubmerchant(submerchantId: number): Promise<CommissionRecordListResponse> {
    const response = await apiClient.get(`/commission-records/by_submerchant/?submerchant_id=${submerchantId}`) as CommissionRecord[]
    return { success: true, message: 'Commission records by submerchant retrieved successfully', data: response }
  },

  // Merchants (for dropdowns)
  async getMerchants(): Promise<MerchantListResponse> {
    const response = await apiClient.get('/merchants/') as any
    return { success: true, message: 'Merchants retrieved successfully', data: response }
  },

  // Settlement Items
  async getSettlementItems(params?: {
    page?: number
    page_size?: number
    settlement?: number
  }): Promise<SettlementItemListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.settlement) queryParams.append('settlement', params.settlement.toString())
    
    const queryString = queryParams.toString()
    const url = queryString ? `/settlement-items/?${queryString}` : '/settlement-items/'
    
    const response = await apiClient.get(url) as SettlementItem[]
    return { success: true, message: 'Settlement items retrieved successfully', data: response }
  },

  async getSettlementItem(id: number): Promise<SettlementItemResponse> {
    const response = await apiClient.get(`/settlement-items/${id}/`) as SettlementItem
    return { success: true, message: 'Settlement item retrieved successfully', data: response }
  },

  async getSettlementItemsBySettlement(settlementId: number): Promise<SettlementItemListResponse> {
    const response = await apiClient.get(`/settlement-items/by_settlement/?settlement_id=${settlementId}`) as SettlementItem[]
    return { success: true, message: 'Settlement items by settlement retrieved successfully', data: response }
  },

  async buildSettlementItemsFromBatch(settlementId: number): Promise<SettlementItemListResponse> {
    const response = await apiClient.post('/settlement-items/build_from_batch/', { settlement_id: settlementId }) as SettlementItem[]
    return { success: true, message: 'Settlement items built from batch successfully', data: response }
  },

  async createSettlementItem(data: Partial<SettlementItem>): Promise<SettlementItemResponse> {
    const response = await apiClient.post('/settlement-items/', data) as SettlementItem
    return { success: true, message: 'Settlement item created successfully', data: response }
  },

  async updateSettlementItem(id: number, data: Partial<SettlementItem>): Promise<SettlementItemResponse> {
    const response = await apiClient.patch(`/settlement-items/${id}/`, data) as SettlementItem
    return { success: true, message: 'Settlement item updated successfully', data: response }
  },

  async deleteSettlementItem(id: number): Promise<{ success: boolean; message: string }> {
    await apiClient.delete(`/settlement-items/${id}/`)
    return { success: true, message: 'Settlement item deleted successfully' }
  },
}